import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Heart, Bed, Users as UsersIcon, Sparkles } from "lucide-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import Eyebrow from "@/components/common/Eyebrow";

function FeaturedVillaCard({ villa }) {
  const { user, refresh } = useAuth();
  const [saved, setSaved] = useState(user?.saved_villas?.includes(villa.id) || false);
  const discounted = villa.discount_percent ? Math.round(villa.price_per_night * (1 - villa.discount_percent / 100)) : villa.price_per_night;
  const isGuestFavorite = villa.rating >= 4.8;
  const isNew = villa.categories?.includes("new");
  const highlight = villa.amenities?.[0] || villa.categories?.[0];

  const handleSave = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast("Please sign in to save villas"); return; }
    try {
      const { data } = await api.post(`/villas/${villa.id}/save`);
      setSaved(data.saved);
      toast(data.saved ? "Saved to your collection" : "Removed from saved");
      refresh();
    } catch { toast("Could not save"); }
  };

  return (
    <Box component={Link} to={`/villas/${villa.id}`} data-testid={`featured-villa-${villa.id}`} sx={{ textDecoration: "none", color: "inherit", display: "block" }}>
      <Box sx={{ position: "relative", aspectRatio: "4 / 3", borderRadius: 3.5, overflow: "hidden", mb: 1.5 }}>
        <Box component="img" src={villa.images?.[0]} alt={villa.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        {isGuestFavorite && (
          <Box sx={{ position: "absolute", top: 10, left: 10, bgcolor: "background.paper", fontSize: 11, fontWeight: 600, px: 1.25, py: 0.5, borderRadius: 999, boxShadow: 2 }}>Guest favourite</Box>
        )}
        {!isGuestFavorite && isNew && (
          <Box sx={{ position: "absolute", top: 10, left: 10, bgcolor: "background.paper", fontSize: 11, fontWeight: 600, px: 1.25, py: 0.5, borderRadius: 999, boxShadow: 2, display: "flex", alignItems: "center", gap: 0.4 }}>
            <Sparkles size={11} className="text-gold" /> New
          </Box>
        )}
        {villa.discount_percent > 0 && (
          <Box className="bg-gold" sx={{ position: "absolute", top: isGuestFavorite || isNew ? 44 : 10, left: 10, fontSize: 10, fontWeight: 600, color: "#fff", px: 1.25, py: 0.5, borderRadius: 999 }}>
            {villa.discount_percent}% off
          </Box>
        )}
        <IconButton
          onClick={handleSave}
          size="small"
          data-testid={`featured-save-${villa.id}`}
          sx={{ position: "absolute", top: 10, right: 10, bgcolor: "rgba(0,0,0,0.35)", color: saved ? "primary.main" : "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.5)" } }}
        >
          <Heart size={15} fill={saved ? "currentColor" : "none"} />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{villa.name}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexShrink: 0 }}>
          <Star size={13} fill="currentColor" />
          <Typography sx={{ fontSize: 13 }}>{villa.rating}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>({villa.reviews_count})</Typography>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{villa.location}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.75, color: "text.secondary", fontSize: 12, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Bed size={13} />{villa.bedrooms} Beds</Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><UsersIcon size={13} />{villa.max_guests} Guests</Box>
        {highlight && <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Sparkles size={13} />{highlight}</Box>}
      </Box>
      <Box sx={{ mt: 0.75, display: "flex", alignItems: "baseline", gap: 0.5 }}>
        {villa.discount_percent > 0 && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", textDecoration: "line-through" }}>₹{villa.price_per_night.toLocaleString()}</Typography>
        )}
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>₹{discounted.toLocaleString()}</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>/ night</Typography>
      </Box>
    </Box>
  );
}

export default function FeaturedVillas() {
  const [villas, setVillas] = useState(null);

  useEffect(() => {
    api.get("/villas?limit=4&sort=featured").then((r) => setVillas(r.data)).catch(() => setVillas([]));
  }, []);

  return (
    <Box component="section" sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, lg: 6 }, pt: { xs: 6, md: 8 } }} data-testid="featured-villas-section">
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Eyebrow sx={{ mb: 1 }}>Featured villas</Eyebrow>
          <Typography component="h2" className="font-serif-display" sx={{ fontSize: { xs: 26, md: 32 } }}>Villas guests can't stop talking about</Typography>
        </Box>
        <Box component={Link} to="/villas" sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 14, fontWeight: 500, color: "text.primary", textDecoration: "none", "&:hover": { color: "primary.main" } }}>
          View all villas <ArrowRight size={14} />
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: { xs: 2, md: 3 } }}>
        {villas === null
          ? [...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" sx={{ aspectRatio: "4 / 3", borderRadius: 3.5 }} />)
          : villas.map((v) => <FeaturedVillaCard key={v.id} villa={v} />)}
      </Box>
    </Box>
  );
}
